import { createRouter, createWebHistory, type Router, type RouteRecordRaw } from 'vue-router'
import { installAuthGuards } from '@/router/guards'
import { isStaleChunkError, navigateOnceForStaleChunks } from '@/utils/chunkLoadRecovery'
import { logError } from '@/utils/safeErrorLog'

const PlaceholderPage = () => import('@/views/PlaceholderPage.vue')

/** A screen a later card ports: the placeholder, titled as the React screen is. */
function placeholder(path: string, title: string): RouteRecordRaw {
  return { path, component: PlaceholderPage, props: { title } }
}

/**
 * The React route table (src/router/routes.tsx on main), path for path. Auth requirements are
 * route meta, enforced by the guards in ./guards.ts; meta on a parent applies to its children.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', component: () => import('@/views/DashboardPage.vue') },
      { path: 'menu/messages', component: () => import('@/views/MessagesPage.vue') },
      { path: 'menu/statement', component: () => import('@/views/StatementPage.vue') },
      placeholder('menu/viewpdf', 'View PDF'),
      { path: 'menu/appointments', component: () => import('@/views/AppointmentsPage.vue') },
      { path: 'menu/billing', component: () => import('@/views/BillingPage.vue') },
      { path: 'menu/documents', component: () => import('@/views/DocumentsPage.vue') },
      { path: 'menu/profile', component: () => import('@/views/ProfilePage.vue') },
      { path: 'menu/tasks', component: () => import('@/views/TasksPage.vue') },
      { path: 'form-handler', component: () => import('@/components/forms/FormHandler.vue') }
    ]
  },
  { path: '/contact', redirect: '/' },
  {
    path: '/complaint',
    component: () => import('@/layouts/ComplaintLayout.vue'),
    meta: { sso: true },
    children: [
      { path: '', redirect: '/complaint/demographics' },
      { path: 'demographics', component: () => import('@/pages/complaint/DemographicsPage.vue') },
      {
        path: 'communication-preferences',
        component: () => import('@/pages/complaint/CommPrefsWizardPage.vue')
      },
      {
        path: 'financial-responsibility',
        component: () => import('@/pages/complaint/FinancialResponsibilityPage.vue')
      },
      {
        path: 'financial-responsibility-insurance',
        component: () => import('@/pages/complaint/FinancialResponsibilityInsurancePage.vue')
      },
      {
        path: 'pediatric-history',
        component: () => import('@/pages/complaint/PediatricHistoryPage.vue')
      },
      {
        path: 'health-insurance',
        component: () => import('@/pages/complaint/HealthInsurancePage.vue')
      },
      {
        path: 'auto-insurance',
        component: () => import('@/pages/complaint/AutoInsurancePage.vue')
      },
      {
        path: 'workers-compensation',
        component: () => import('@/pages/complaint/WorkersCompensationPage.vue')
      },
      {
        path: 'primary-complaint',
        component: () => import('@/pages/complaint/PrimaryComplaintPage.vue')
      },
      {
        path: 'complaint-history',
        component: () => import('@/pages/complaint/ComplaintHistoryPage.vue')
      },
      {
        path: 'complaint-factors',
        component: () => import('@/pages/complaint/ComplaintFactorsPage.vue')
      },
      placeholder('secondary-complaint', 'Secondary Complaint'),
      {
        path: 'complaint-summary',
        component: () => import('@/pages/complaint/ComplaintSummaryPage.vue')
      },
      {
        path: 'headache-history',
        component: () => import('@/pages/complaint/HeadacheHistoryPage.vue')
      },
      // "assesment" (sic): the live URLs, kept as React has them.
      {
        path: 'outcome-assesment',
        component: () => import('@/pages/complaint/OutcomeAssessmentPage.vue')
      },
      {
        path: 'outcome-assesment-new',
        component: () => import('@/pages/complaint/OutcomeAssessmentPage.vue')
      },
      {
        path: 'outcome-assesment2',
        component: () => import('@/pages/complaint/OutcomeAssessmentPage.vue')
      },
      { path: 'dizziness', component: () => import('@/pages/complaint/DizzinessPage.vue') },
      { path: 'wellness', component: () => import('@/pages/complaint/WellnessPage.vue') },
      {
        path: 'health-history',
        component: () => import('@/pages/complaint/HealthHistory1Page.vue')
      },
      {
        path: 'health-history-2',
        component: () => import('@/pages/complaint/HealthHistory2Page.vue')
      },
      {
        path: 'health-history-3',
        component: () => import('@/pages/complaint/HealthHistory3Page.vue')
      },
      {
        path: 'family-history',
        component: () => import('@/pages/complaint/FamilyHistoryPage.vue')
      },
      // One component, two routes: ComplaintLayout keys its RouterView on the path, so each remounts.
      { path: 'portal-config', component: () => import('@/pages/complaint/PortalConfigPage.vue') },
      {
        path: 'general-history',
        component: () => import('@/pages/complaint/PortalConfigPage.vue')
      },
      { path: 'task-complete', component: () => import('@/pages/complaint/TaskCompletePage.vue') },
      {
        path: 'insurance-complete',
        component: () => import('@/pages/complaint/InsuranceCompletePage.vue')
      }
    ]
  },
  {
    ...placeholder('/statement/payment/new-card', 'Fortis Payment'),
    meta: { requiresAuth: true }
  },
  {
    path: '/account',
    component: () => import('@/layouts/AccountLayout.vue'),
    children: [
      { path: '', redirect: '/account/login' },
      {
        path: 'login',
        component: () => import('@/pages/account/LoginPage.vue'),
        meta: { redirectIfAuth: true }
      },
      { path: 'requestpin', component: () => import('@/pages/account/RequestPinPage.vue') },
      { path: 'forgotpassword', component: () => import('@/pages/account/ForgotPasswordPage.vue') }
    ]
  },
  {
    // No component: React's bare <Outlet />, so each child renders on its own.
    path: '/task',
    children: [
      { path: '', component: () => import('@/pages/task/TaskLinkPage.vue') },
      {
        path: 'fv',
        component: () => import('@/layouts/TaskLayout.vue'),
        meta: { requiresAuth: true },
        children: [{ path: '', component: () => import('@/pages/task/FvTaskPage.vue') }]
      }
    ]
  },
  placeholder('/sso-handler', 'SSO Handler'),
  { path: '/:pathMatch(.*)*', component: () => import('@/views/NotFoundPage.vue') }
]

/**
 * A factory, not a module singleton: `createWebHistory` reads the URL when it is created, and
 * `initBrand()` must strip the `/hs` and `?hs` markers before that (see main.ts).
 */
export function createAppRouter(): Router {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
  })

  installAuthGuards(router)

  // Stale hashed chunks after deploy: land on the clicked route with a fresh shell. Sentry's
  // router integration reports router errors itself (and drops stale chunks in beforeSend).
  router.onError((error, to) => {
    if (isStaleChunkError(error)) {
      navigateOnceForStaleChunks(to.fullPath)
      return
    }
    logError('[router]', error)
  })

  return router
}
